/**
 * ============================================================
 * PGLP | MONTE HERMOM
 * SERVICE: AuditoriaNormativaService
 * ------------------------------------------------------------
 * Hub ÚNICO de validação do laudo.
 *
 * AGRUPA:
 *   A. Fatores regionais (NBR 14653-2)
 *      - Faixa de fator, desvio, justificativa, evidências
 *   B. Prazo judicial
 *      - Definição + documento comprobatório
 *   C. Tarefas operacionais
 *      - Atrasos, prazos vencidos
 *   D. Comunicação pré-diligência
 *      - Tentativas mínimas (3), intervalos de 24/48h
 *
 * REGRAS:
 *   ✔ Nunca retorna undefined
 *   ✔ Nunca altera dados
 *   ✔ Sempre devolve objeto estruturado
 *   ✔ Compatível com `laudo.mensagens.js`
 *
 * VERSÃO: 3.0.0
 * ============================================================
 */

import { LIMITES } from '../constants/vistoria';

/* =========================
   STORAGE (tarefas / diligências)
========================= */

const KEY_TAREFAS = 'pglp_tarefas';
const KEY_DILIGENCIAS = 'pglp_diligencias';

function lerLocalStorage(chave) {
  try {
    return JSON.parse(localStorage.getItem(chave)) || [];
  } catch {
    return [];
  }
}

/* =========================
   A. VALIDAÇÃO DE FATOR REGIONAL
========================= */

/**
 * Valida um fator regional individual (F01–F06).
 *
 * @param {object} dados
 * @returns {{
 *   isBloqueado: boolean,
 *   mensagem: string,
 *   indicePercentual: number,
 *   precisaJustificar: boolean,
 *   pendencias: string[],
 * }}
 */
function validarFinalizacao(dados = {}) {
  const pendencias = [];

  /* ---------- 1. Fator numérico válido ---------- */
  const fator = parseFloat(dados.fatorValorizacao);
  if (Number.isNaN(fator) || fator <= 0) {
    pendencias.push('Fator de valorização inválido ou ausente');
    return {
      isBloqueado: true,
      mensagem: pendencias[0],
      indicePercentual: 0,
      precisaJustificar: false,
      pendencias,
    };
  }

  /* ---------- 2. Faixa normativa ---------- */
  if (fator < LIMITES.FATOR_MIN || fator > LIMITES.FATOR_MAX) {
    pendencias.push(
      `Fator ${fator.toFixed(2)} fora da faixa normativa ` +
      `(${LIMITES.FATOR_MIN} – ${LIMITES.FATOR_MAX})`
    );
  }

  /* ---------- 3. Desvio percentual ---------- */
  const desvio = Math.abs((fator - 1) * 100);
  const precisaJustificar = desvio > LIMITES.DESVIO_JUSTIFICATIVA_PCT;
  const critico = desvio > LIMITES.DESVIO_BLOQUEIO_PCT;

  if (critico) {
    pendencias.push(
      `Desvio crítico de ${desvio.toFixed(2)}% — revisar base de cálculo`
    );
  }

  /* ---------- 4. Justificativa textual ---------- */
  const textoJustificativa = String(dados.justificativaResumo || '').trim();
  if (precisaJustificar && textoJustificativa.length < 20) {
    pendencias.push(
      `Desvio de ${desvio.toFixed(2)}% exige justificativa técnica (mínimo 20 caracteres)`
    );
  }

  /* ---------- 5. Evidências fotográficas ---------- */
  const totalEvidencias = Object.keys(dados.evidencias || {})
    .filter(k => dados.evidencias[k]).length;
  if (totalEvidencias === 0) {
    pendencias.push('Nenhuma evidência fotográfica registrada neste módulo');
  }

  /* ---------- 6. Cobertura de justificativas de divergência ---------- */
  const divergencias = Array.isArray(dados._divergencias) ? dados._divergencias : [];
  const justificativasPreenchidas = Object.entries(dados.justificativas || {})
    .filter(([, v]) => v && String(v).trim().length > 0)
    .map(([k]) => k);

  const divergenciasSemTexto = divergencias.filter(
    campo => !justificativasPreenchidas.includes(campo)
  );

  if (divergenciasSemTexto.length > 0) {
    pendencias.push(
      `${divergenciasSemTexto.length} campo(s) divergente(s) sem justificativa: ` +
      divergenciasSemTexto.join(', ')
    );
  }

  /* ---------- Índice de conformidade ---------- */
  const indicePercentual = Math.max(0, 100 - pendencias.length * 20);

  return {
    isBloqueado: pendencias.length > 0,
    mensagem: pendencias[0] || '',
    indicePercentual,
    precisaJustificar,
    pendencias,
  };
}

/* =========================
   A. AUDITORIA COMPLETA DOS 6 FATORES
========================= */

const FATORES_ORDEM = [
  'f01_servicos',
  'f02_infra',
  'f03_seguranca',
  'f04_acessibilidade',
  'f05_comercio',
  'f06_ocupacao',
];

function auditarLaudoCompleto(laudo = {}) {
  const detalhes = FATORES_ORDEM.map(id => ({
    id,
    ...validarFinalizacao(laudo[id] || {}),
  }));

  const bloqueados = detalhes.filter(d => d.isBloqueado).map(d => d.id);

  const naoRevisados = FATORES_ORDEM.filter(
    id => laudo[id]?.statusRevisao !== 'REVISADO'
  );

  const revisados = FATORES_ORDEM.length - naoRevisados.length;

  const indiceGlobal = Math.round(
    detalhes.reduce((soma, d) => soma + d.indicePercentual, 0) /
    FATORES_ORDEM.length
  );

  return {
    podeProtocolar: bloqueados.length === 0 && naoRevisados.length === 0,
    totalFatores: FATORES_ORDEM.length,
    revisados,
    bloqueados,
    naoRevisados,
    detalhes,
    indiceGlobal,
  };
}

/* =========================
   B. VALIDAÇÃO DE PRAZO JUDICIAL
========================= */

/**
 * @param {object} laudo
 * @returns {string[]} códigos de bloqueio
 */
function validarPrazoLaudo(laudo) {
  const bloqueios = [];

  if (!laudo) {
    return ['LAUDO_INVALIDO'];
  }

  if (!laudo.prazoDias || !laudo.dataInicio) {
    bloqueios.push('PRAZO_NAO_DEFINIDO');
  }

  if (laudo.prazoDias && !laudo.prazoDocumentoUrl) {
    bloqueios.push('PRAZO_SEM_DOCUMENTO');
  }

  return bloqueios;
}

/* =========================
   C. VALIDAÇÃO DE TAREFAS
========================= */

function validarTarefasLaudo(ordemId) {
  const tarefas = lerLocalStorage(KEY_TAREFAS);
  const hoje = new Date();

  const tarefasLaudo = tarefas.filter(t => t.laudoId === ordemId);

  const temAtraso = tarefasLaudo.some(t =>
    t.status !== 'CONCLUIDA' &&
    t.status !== 'CONCLUIDO' &&
    t.prazo &&
    new Date(t.prazo) < hoje
  );

  return temAtraso ? ['TAREFA_ATRASADA'] : [];
}

/* =========================
   D. VALIDAÇÃO DE COMUNICAÇÃO
========================= */

function validarComunicacao(ordemId) {
  const dados = lerLocalStorage(KEY_DILIGENCIAS);
  const diligencias = dados.filter(d => d.ordemId === ordemId);
  const comunicacoes = diligencias.filter(d => d.tipo === 'COMUNICACAO');

  const bloqueios = [];

  if (comunicacoes.length === 0) {
    bloqueios.push('SEM_COMUNICACAO');
  }

  if (comunicacoes.length > 0 && comunicacoes.length < 3) {
    bloqueios.push('TENTATIVAS_INSUFICIENTES');
  }

  return bloqueios;
}

/* =========================
   BLOQUEIO GERAL (consolidado)
========================= */

/**
 * Validação unificada de prazo + tarefas + comunicação.
 *
 * @param {object} args
 * @param {object} args.laudo
 * @param {string} args.ordemId
 * @returns {{
 *   bloqueado: boolean,
 *   bloqueios: string[],
 *   total: number,
 * }}
 */
function validarLaudoCompleto({ laudo, ordemId }) {
  const bloqueios = [
    ...validarPrazoLaudo(laudo),
    ...validarTarefasLaudo(ordemId),
    ...validarComunicacao(ordemId),
  ];

  return {
    bloqueado: bloqueios.length > 0,
    bloqueios,
    total: bloqueios.length,
  };
}

/* =========================
   HELPERS DE CÁLCULO
========================= */

function calcularDesvio(fator) {
  const f = parseFloat(fator);
  if (Number.isNaN(f)) return 0;
  return +((f - 1) * 100).toFixed(2);
}

function validarFaixaFator(fator) {
  const f = parseFloat(fator);
  if (Number.isNaN(f)) return { valido: false, motivo: 'Fator inválido' };
  if (f < LIMITES.FATOR_MIN) return { valido: false, motivo: 'Abaixo do mínimo' };
  if (f > LIMITES.FATOR_MAX) return { valido: false, motivo: 'Acima do máximo' };
  return { valido: true, motivo: null };
}

/* =========================
   API UNIFICADA
========================= */

export const AuditoriaNormativaService = {
  /* A. Fatores regionais */
  validarFinalizacao,
  auditarLaudoCompleto,

  /* B/C/D. Prazo, tarefas, comunicação */
  validarPrazoLaudo,
  validarTarefasLaudo,
  validarComunicacao,
  validarLaudoCompleto,

  /* Helpers */
  calcularDesvio,
  validarFaixaFator,
};

export default AuditoriaNormativaService;